import { IWaterSource } from '@/api/config/waterSourceApi'
import SortIcon from '@/components/icons/SortIcon'
import { SortBy } from '@/constant/common'
import useUser from '@/features/auth/hooks/useUser'
import useTranslation from '@/hooks/useTranslation'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Stack } from '@mui/system'
import { lowerCase, snakeCase } from 'lodash'
import React, { FC } from 'react'
import { useLocation } from 'react-router-dom'
import WaterSourceTableRow from './WaterSourceTableRow'
import classes from './style.module.scss'

interface WaterSourceTableProps {
  rows: IWaterSource[]
  onDelete: (
    id: number | string,
    interfaceId: string | number,
    name: string,
  ) => void
  handleEsc: (sortBy: SortBy) => void
  handleDesc: (sortBy: SortBy) => void
}

interface ITableHeaderProps {
  [key: string]: {
    label: string
    style?: object
    showUnitLabel?: boolean
    stack?: (arg: ITableHeaderIconProps) => React.ReactNode
  }
}

interface ITableHeaderIconProps {
  handleEsc: (sortBy: SortBy) => void
  handleDesc: (sortBy: SortBy) => void
  sortBy: SortBy
  isDesc?: boolean
  isSorted?: boolean
}

const WaterSourceTable: FC<WaterSourceTableProps> = ({
  rows,
  onDelete,
  handleDesc,
  handleEsc,
}) => {
  const { t } = useTranslation()

  const { data: user } = useUser()

  const location = useLocation()
  const searchParamsFromURL = new URLSearchParams(location.search)

  const isMetric = !user?.measure?.id || user?.measure?.id === 1
  const unitLabelWater = `$/${isMetric ? 'm³' : 'kGal'}`

  const WaterSourceTableHeaders: ITableHeaderProps = {
    spaceLeft: {
      label: '',
      style: {
        width: '5%',
        paddingLeft: '54px',
      },
      stack: ({
        sortBy,
        isDesc,
        isSorted,
        handleEsc,
        handleDesc,
      }: ITableHeaderIconProps) => {
        return (
          <Stack direction="row" spacing={0}>
            <SortIcon
              handleEsc={handleEsc}
              handleDesc={handleDesc}
              sortBy={sortBy}
              isDesc={isDesc}
              isSorted={isSorted}
            />
            <Typography className={classes.txtFertigationHead}></Typography>
          </Stack>
        )
      },
    },
    order: {
      label: '#',
      style: {
        width: '5%',
      },
    },
    waterSourceName: {
      label: t('waterSourceName'),
      style: {
        width: '20%',
        borderRight: '1px solid #d7d7d7',
      },
    },
    flowValveIO: {
      label: t('waterSourceTableHeaderFlowValveIO'),
      style: {
        width: '20%',
        borderRight: '1px solid #d7d7d7',
      },
    },
    flowValveType: {
      label: t('flowValveType'),
      style: {
        width: '15%',
        borderRight: '1px solid #d7d7d7',
      },
    },
    flowSensor: {
      label: t('flowSensor'),
      style: {
        width: '15%',
        borderRight: '1px solid #d7d7d7',
      },
    },
    waterCost: {
      label: t('waterCost'),
      showUnitLabel: true,
      style: {
        width: '10%',
        borderRight: '1px solid #d7d7d7',
      },
    },
    action: {
      label: t('commonTableHeaderAction'),
      style: {
        width: 'calc(100% - 90%)',
      },
    },
  }

  return (
    <Stack flexGrow="1">
      <TableContainer component={Paper}>
        <Table className={classes.table} aria-label="simple table">
          <TableHead>
            <TableRow>
              {Object.entries(WaterSourceTableHeaders).map(
                ([key, waterSource]) => {
                  return (
                    <TableCell
                      key={key + snakeCase(lowerCase(waterSource.label))}
                      sx={waterSource.style}
                    >
                      {waterSource?.stack
                        ? waterSource.stack({
                            isDesc:
                              searchParamsFromURL.get('SortBy') === SortBy.ID &&
                              searchParamsFromURL.get('SortDirection') ===
                                'desc',
                            isSorted:
                              searchParamsFromURL.get('SortBy') === SortBy.ID &&
                              searchParamsFromURL.get('SortDirection') ===
                                'asc',
                            sortBy: SortBy.ID,
                            handleDesc: handleDesc,
                            handleEsc: handleEsc,
                          })
                        : waterSource.label}{' '}
                      {waterSource?.showUnitLabel ? `(${unitLabelWater})` : ''}
                    </TableCell>
                  )
                },
              )}
            </TableRow>
          </TableHead>

          <TableBody className={classes.tableBody}>
            {rows?.map((row) => (
              <WaterSourceTableRow
                key={row.id}
                row={row}
                handleDeleteItem={onDelete}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default WaterSourceTable
